// PRD 07 §2.2 #12 / §3.2 — 3-tier Area → Sub-area → Item cascade.
// Selecting Area resets Sub-area + Item; selecting Sub-area resets Item. The
// downstream selects are disabled until their parent is chosen. Matches the
// prototype `syncSubAreas` / `syncItems` flow.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Select } from '../../../components/ui/Select';
import type { ProTopicNode } from '../types/management-guidelines.types';
import { getCategories, getItems, getSubAreas } from '../utils/topic-tree';

interface CascadingTopicSelectProps {
  topics: ProTopicNode[];
  category: string;
  subArea: string;
  item: string;
  onChange: (next: { category: string; subArea: string; item: string }) => void;
  disabled?: boolean;
}

export function CascadingTopicSelect({
  topics,
  category,
  subArea,
  item,
  onChange,
  disabled,
}: CascadingTopicSelectProps) {
  const { t } = useTranslation('management-guidelines');

  const categories = useMemo(() => getCategories(topics), [topics]);
  const subAreas = useMemo(() => getSubAreas(topics, category), [topics, category]);
  const items = useMemo(
    () => getItems(topics, category, subArea),
    [topics, category, subArea],
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 8,
      }}
    >
      <Select
        options={categories}
        value={category}
        onChange={(value) => onChange({ category: value, subArea: '', item: '' })}
        disabled={disabled}
        placeholder={t('form.fields.category.placeholder')}
        ariaLabel={t('form.fields.category.label')}
      />

      <Select
        options={subAreas}
        value={subArea}
        onChange={(value) => onChange({ category, subArea: value, item: '' })}
        disabled={disabled || !category}
        placeholder={t('form.fields.subArea.placeholder')}
        ariaLabel={t('form.fields.subArea.label')}
      />

      <Select
        options={items}
        value={item}
        onChange={(value) => onChange({ category, subArea, item: value })}
        disabled={disabled || !subArea}
        placeholder={t('form.fields.item.placeholder')}
        ariaLabel={t('form.fields.item.label')}
      />
    </div>
  );
}
